import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useOutletContext,
} from "react-router-dom";

import {
  AuthCallback,
  AuthUpdate,
  EmailConfirmation,
  SignIn,
  SignUpRoute,
  Waitlist,
} from "./components/Auth";
import { BAAPage } from "./components/BAA";
import { BannerNotificationBar } from "./components/BannerNotification";
import { BillingAlertBanner } from "./components/BillingAlert";
import CaseManagement from "./components/CaseManagement";
import { DocumentAccess } from "./components/DocumentAccess";
import FileManagement from "./components/FileManagement";
import { FirmSettings } from "./components/FirmSettings";
import { LandingPage } from "./components/LandingPage";
import Layout from "./components/Layout";
import { MedicalChronology } from "./components/MedicalChronology";
import { PageLoadingSpinner } from "./components/PageLoadingSpinner";
import PhysicianManagement from "./components/PhysicianManagement";
import { PrivacyPolicyPage } from "./components/PrivacyPolicy";
import { ProfilePage } from "./components/Profile";
import { ProtectedRoute } from "./components/ProtectedRoute";
import ReviewPreferences from "./components/ReviewPreferences";
import { SubscriptionPage } from "./components/Subscription";
import { Tutorials } from "./components/Tutorials";
import UserManagement from "./components/UserManagement";
import { AUTH_PATHS, DEFAULT_PROTECTED_ROUTE_PATH, WAITLIST_PATH } from "./constants";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// import.meta.env.DEV is statically replaced by Vite, letting Rollup tree-shake this page out of prod builds
const DesignSystemPage = import.meta.env.DEV
  ? lazy(() => import("./pages/DesignSystem").then((m) => ({ default: m.DesignSystemPage })))
  : null;

const handleUnhandledRejection = (event) => {
  console.error("[Global] Unhandled Promise Rejection:", {
    reason: event.reason,
    stack: event.reason?.stack,
    location: window.location.href,
    reasonType: Object.prototype.toString.call(event.reason),
    path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
};

window.addEventListener("unhandledrejection", handleUnhandledRejection);

const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoadingSpinner />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to={DEFAULT_PROTECTED_ROUTE_PATH} replace /> : <LandingPage />}
      />
      <Route path={AUTH_PATHS.SIGNIN} element={<SignIn />} />
      <Route path={AUTH_PATHS.SIGNUP} element={<SignUpRoute />} />
      <Route path={WAITLIST_PATH} element={<Waitlist />} />
      <Route path="/tutorials" element={<Tutorials />} />
      <Route path="/email-confirmation" element={<EmailConfirmation />} />
      <Route path={AUTH_PATHS.CALLBACK} element={<AuthCallback />} />
      <Route path="/baa" element={<BAAPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      {import.meta.env.DEV && (
        <Route
          path="/design-system"
          element={
            <Suspense fallback={null}>
              <DesignSystemPage />
            </Suspense>
          }
        />
      )}

      <Route element={<ProtectedRoute />}>
        <Route path={AUTH_PATHS.UPDATE} element={<AuthUpdate />} />
        <Route path="/docs/:docId" element={<DocumentAccess />} />
        <Route
          element={
            <div className="sticky top-0 z-sticky flex flex-col">
              <BannerNotificationBar />
              <BillingAlertBanner />
              <Layout>
                <Outlet />
              </Layout>
            </div>
          }
        >
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/cases" element={<CaseManagement />} />
          <Route path="/cases/:caseId/files" element={<FileManagementWrapper />} />
          <Route path="/cases/:caseId/chronology" element={<MedicalChronology />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/firm-settings" element={<FirmSettings />} />
          <Route path="/preferences" element={<ReviewPreferences />} />
          <Route path="/preferences/document-types/:documentType" element={<ReviewPreferences />} />
          <Route
            path="/preferences/document-types/:documentType/visit-types/:visitType"
            element={<ReviewPreferences />}
          />
          <Route path="/physicians" element={<PhysicianManagement />} />
          <Route path="/templates" element={<ReviewPreferences />} />
          <Route path="/templates/:templateId" element={<ReviewPreferences />} />
          <Route
            path="/templates/:templateId/document-types/:documentType"
            element={<ReviewPreferences />}
          />
          <Route
            path="/templates/:templateId/document-types/:documentType/visit-types/:visitType"
            element={<ReviewPreferences />}
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const FileManagementWrapper = () => {
  const context = useOutletContext();
  return <FileManagement {...context} />;
};

const App = () => {
  useEffect(() => {
    const handleError = (event) => {
      console.error("[App] Error caught:", {
        error: event.error,
        message: event.message,
        stack: event.error?.stack,
        location: window.location.href,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return (
    <>
      <Toaster position="top-right" />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </>
  );
};

export default App;
